import React, { useRef, useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface AddProductModalProps {
  onClose: () => void;
  onAdd: (product: {
    id: string;
    name: string;
    category: string;
    image: string;
    sku: string;
    price: string;
  }) => void;
}

export const AddProductModal: React.FC<AddProductModalProps> = ({ onClose, onAdd }) => {
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const imgInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    const fallback = `https://placehold.co/400x400/f8fafc/64748b?text=${encodeURIComponent(name.substring(0, 15))}`;
    const prod = {
      id: `p_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name.trim(),
      category: 'General',
      image: imagePreview || fallback,
      sku: sku.trim() || `SKU-${Math.floor(Math.random() * 100000)}`,
      price: price.trim() || `$${(Math.random() * 50 + 5).toFixed(2)}`
    };
    onAdd(prod);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/10 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card rounded-2xl shadow-2xl p-6 w-full max-w-sm border border-border">
        <h3 className="font-black uppercase tracking-tight text-lg mb-4 text-foreground">Add Product</h3>
        <div className="space-y-3">
          <div
            onClick={() => imgInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="relative w-full aspect-video rounded-xl border-2 border-dashed border-border bg-muted flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all overflow-hidden group"
          >
            {imagePreview ? (
              <>
                <img src={imagePreview} alt="preview" className="w-full h-full object-contain" />
                <div className="absolute inset-0 bg-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-card text-xs font-black uppercase">Change Image</span>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-border flex items-center justify-center mb-2">
                  <Plus size={20} className="text-muted-foreground" />
                </div>
                <p className="text-xs font-black text-muted-foreground uppercase">Upload Product Image</p>
                <p className="text-[10px] text-muted-foreground/60 mt-0.5">Click or drag & drop · JPG, PNG, WEBP</p>
              </>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            ref={imgInputRef}
            onChange={handleImageChange}
          />

          <Input
            autoFocus
            placeholder="Product name *"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          />
          <div className="flex gap-2">
            <Input
              placeholder="SKU (optional)"
              value={sku}
              onChange={e => setSku(e.target.value)}
              className="flex-1"
            />
            <Input
              placeholder="$0.00"
              value={price}
              onChange={e => setPrice(e.target.value)}
              className="w-24"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={onClose} variant="outline" className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || loading}
            className="flex-1"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : 'Add Product'}
          </Button>
        </div>
      </div>
    </div>
  );
};
